import { useState } from 'react';
import { Plus, ShieldCheck, UserX } from 'lucide-react';
import { toast } from 'sonner';
import { useAdminUserMutation, useAdminUsers, useMe } from '../../api/hooks';
import { formatBytes, formatDate } from '../../lib/format';
import {
  Button,
  Checkbox,
  Field,
  Input,
  Modal,
  Pill,
  Spinner,
  useConfirm,
} from '../../components/ui';
import type { AdminUserDto } from '../../api/types';

export function AdminUsers() {
  const { data, isLoading } = useAdminUsers();
  const mutate = useAdminUserMutation();
  const me = useMe();
  const confirm = useConfirm();
  const [editing, setEditing] = useState<AdminUserDto | 'new' | null>(null);

  const remove = async (user: AdminUserDto) => {
    const ok = await confirm({
      title: `Delete ${user.username}?`,
      body: 'Their account is removed permanently. This cannot be undone.',
    });
    if (!ok) return;
    try {
      await mutate.mutateAsync({ op: 'delete', id: user.id });
      toast.success('User deleted');
    } catch (error) {
      toast.error('Could not delete', { description: (error as Error).message });
    }
  };

  return (
    <div className="flex min-h-full flex-col">
      <div className="flex items-center gap-2 border-b border-line bg-surface px-3 py-3 sm:px-4">
        <h1 className="flex-1 text-lg font-semibold">Accounts</h1>
        <Button variant="primary" onClick={() => setEditing('new')}>
          <Plus className="size-4" aria-hidden />
          New account
        </Button>
      </div>

      {isLoading ? (
        <Spinner />
      ) : (
        <ul className="divide-y divide-line">
          {data?.items.map((user) => (
            <li key={user.id} className="flex flex-wrap items-center gap-3 px-3 py-3 sm:px-4">
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-2 truncate font-medium">
                  {user.displayName}
                  {user.isRoot ? <Pill tone="brand">root</Pill> : null}
                  {user.isAdmin && !user.isRoot ? <Pill tone="brand">admin</Pill> : null}
                  {!user.isActive ? <Pill tone="warn">disabled</Pill> : null}
                </p>
                <p className="truncate text-xs text-ink-dim">
                  @{user.username} · {user.fileCount} file{user.fileCount === 1 ? '' : 's'} ·{' '}
                  {formatBytes(user.sizeBytes)} · joined {formatDate(user.createdAt)}
                </p>
              </div>
              <Button onClick={() => setEditing(user)}>Edit</Button>
              {!user.isRoot && user.id !== me.data?.user?.id ? (
                <Button variant="ghost" aria-label={`Delete ${user.username}`} onClick={() => remove(user)}>
                  <UserX className="size-4 text-danger" />
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {editing ? (
        <UserDialog
          user={editing === 'new' ? null : editing}
          currentUserId={me.data?.user?.id ?? ''}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}

function UserDialog({
  user,
  currentUserId,
  onClose,
}: {
  user: AdminUserDto | null;
  currentUserId: string;
  onClose: () => void;
}) {
  const mutate = useAdminUserMutation();
  const [username, setUsername] = useState(user?.username ?? '');
  const [displayName, setDisplayName] = useState(user?.displayName ?? '');
  const [password, setPassword] = useState('');
  const [isAdmin, setIsAdmin] = useState(user?.isAdmin ?? false);
  const [isActive, setIsActive] = useState(user?.isActive ?? true);

  // The server enforces these too - a disabled control simply is not sent -
  // but greying them out explains why they cannot be changed.
  const isSelf = user?.id === currentUserId;
  const locked = Boolean(user?.isRoot || isSelf);

  const save = async () => {
    try {
      if (user) {
        await mutate.mutateAsync({
          op: 'update',
          id: user.id,
          username,
          displayName,
          password: password || undefined,
          isAdmin,
          isActive,
        });
      } else {
        await mutate.mutateAsync({ op: 'create', username, displayName, password, isAdmin });
      }
      toast.success('Saved');
      onClose();
    } catch (error) {
      toast.error('Could not save', { description: (error as Error).message });
    }
  };

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title={user ? `Edit ${user.username}` : 'New account'}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={mutate.isPending} onClick={save}>
            Save
          </Button>
        </>
      }
    >
      <Field label="Display name">
        <Input value={displayName} onChange={(event) => setDisplayName(event.target.value)} />
      </Field>
      <Field label="Username">
        <Input
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          autoComplete="off"
        />
      </Field>
      <Field label={user ? 'New password (leave blank to keep the current one)' : 'Password'}>
        <Input
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="new-password"
          placeholder={user ? 'Unchanged' : 'At least 8 characters'}
        />
      </Field>

      <div className="flex flex-col gap-3">
        <Checkbox
          checked={isAdmin}
          onCheckedChange={locked ? () => undefined : setIsAdmin}
          label={
            <span className={locked ? 'text-ink-dim' : undefined}>
              <ShieldCheck className="mr-1 inline size-4" aria-hidden />
              Administrator
            </span>
          }
        />
        {user ? (
          <Checkbox
            checked={isActive}
            onCheckedChange={locked ? () => undefined : setIsActive}
            label={<span className={locked ? 'text-ink-dim' : undefined}>Account is active</span>}
          />
        ) : null}
        {locked ? (
          <p className="text-xs text-ink-dim">
            {user?.isRoot
              ? 'The root account always stays an active administrator.'
              : 'You cannot remove your own access.'}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
